/** @jsx jsx */
import React, { type RefObject, useEffect, useRef, useState } from 'react';
import { css, styled, jsx } from '@compiled/react';
import noop from 'lodash/noop';
import {
	fetchQuery,
	graphql,
	useFragment,
	useMutation,
	useQueryLoader,
	useRelayEnvironment,
} from 'react-relay';
import { LoadingButton } from '@atlaskit/button';
import CheckIcon from '@atlaskit/icon/core/migration/check-mark--check';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { PopupSelect, type SelectProps, components } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import RELEASE_NOTES_QUERY, {
	type uiReleaseNotesModalQuery,
} from '@atlassian/jira-relay/src/__generated__/uiReleaseNotesModalQuery.graphql';
import type {
	JiraVersionStatus,
	versionStatus_softwareReleasesLaunchCard$key,
} from '@atlassian/jira-relay/src/__generated__/versionStatus_softwareReleasesLaunchCard.graphql';
import type { versionStatus_updateVersionArchivedMutation as UpdateVersionArchivedMutation } from '@atlassian/jira-relay/src/__generated__/versionStatus_updateVersionArchivedMutation.graphql';
import type { versionStatus_updateVersionReleasedMutation as UpdateVersionReleasedMutation } from '@atlassian/jira-relay/src/__generated__/versionStatus_updateVersionReleasedMutation.graphql';
import type { versionStatus_versionHasReleaseNoteQuery } from '@atlassian/jira-relay/src/__generated__/versionStatus_versionHasReleaseNoteQuery.graphql';
import { isDevopsFeatureDisabledInFedRamp } from '@atlassian/jira-software-devops-fedramp-utils/src/index.tsx';
import { ReleaseNotesModal } from '@atlassian/jira-software-releases-add-release-notes-modal/src/ui/async.tsx';
import { useRefetchContext } from '@atlassian/jira-software-releases-version-detail-refetch-util/src/index.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import type {
	Version,
	SuccessParams,
} from '@atlassian/jira-version-modals/src/common/model/index.tsx';
import ReleaseModal from '@atlassian/jira-version-modals/src/release-version-modal/index.tsx';
import { ARCHIVED, RELEASED, UNARCHIVED, UNRELEASED } from '../../common/constants.tsx';
import type { VersionStatusOption } from '../../common/types.tsx';
import { NonCriticalErrorBoundary } from '../../common/utils/non-critical-error-boundary/NonCriticalErrorBoundary.tsx';
import messages from './messages.tsx';

type Props = {
	jiraVersion: versionStatus_softwareReleasesLaunchCard$key;
};

const overrideComponents: SelectProps<VersionStatusOption, false>['components'] = {
	Option: (props) => <components.Option {...props} css={styles} />,
};

const CustomOptionLabel = ({ label, value }: { label: string; value: string }) => (
	<VersionStatusOptionLabel isArchiveOption={value === ARCHIVED}>{label}</VersionStatusOptionLabel>
);

export const GetCurrentStatusLabel = (currentStatusValue: JiraVersionStatus) => {
	const { formatMessage } = useIntl();
	switch (currentStatusValue) {
		case ARCHIVED:
			return formatMessage(messages.archivedLabel);
		case RELEASED:
			return formatMessage(messages.releasedLabel);
		case UNRELEASED:
			return formatMessage(messages.unreleasedLabel);
		default:
			return formatMessage(messages.unreleasedLabel);
	}
};

const getHasReleaseNote = async (
	versionId: string,
	environment: ReturnType<typeof useRelayEnvironment>,
) => {
	try {
		const data = await fetchQuery<versionStatus_versionHasReleaseNoteQuery>(
			environment,
			graphql`
				query versionStatus_versionHasReleaseNoteQuery($versionId: ID!) {
					jira {
						version(id: $versionId) {
							... on JiraVersion {
								hasReleaseNote
							}
						}
					}
				}
			`,
			{ versionId },
		).toPromise();
		return data?.jira?.version?.hasReleaseNote ?? false;
	} catch (error) {
		fireErrorAnalytics({
			meta: {
				id: 'jiraLaunchCardHasReleaseNoteQuery',
				packageName: 'jiraVersionLaunchCard',
				teamName: 'nova',
			},
			error: new Error('Unable to get "hasReleaseNote" for "release version" modal'),
		});
		return false;
	}
};

const useHasReleaseNote = (
	versionId: string,
	shouldLoadHasReleaseNote: boolean,
): boolean | null => {
	const [hasReleaseNote, setHasReleaseNote] = useState<boolean | null>(null);
	const environment = useRelayEnvironment();
	useEffect(() => {
		if (shouldLoadHasReleaseNote) {
			getHasReleaseNote(versionId, environment).then(setHasReleaseNote);
		}
	}, [versionId, environment, shouldLoadHasReleaseNote]);
	return hasReleaseNote;
};

export const VersionStatus = ({ jiraVersion }: Props) => {
	const { formatMessage } = useIntl();
	const [isReleasedModalOpen, setIsReleaseModalOpen] = useState(false);
	const { refetch, isLoading: isRefetchLoading } = useRefetchContext();
	const locale = useLocale();
	const { showFlag } = useFlagsService();
	const [selectWidth, setSelectWidth] = useState<number | undefined>(undefined);
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const selectRef = useRef() as RefObject<HTMLDivElement>;
	const [isReleaseNotesModalOpen, setIsReleaseNotesModalOpen] = useState<boolean>(false);

	useEffect(() => {
		if (selectRef.current) {
			setSelectWidth(selectRef.current.offsetWidth);
		}
	}, []);

	const version = useFragment(
		graphql`
			fragment versionStatus_softwareReleasesLaunchCard on JiraVersion {
				id @required(action: THROW)
				versionId @required(action: THROW)
				status @required(action: THROW)
				project @required(action: THROW) {
					key @required(action: THROW)
				}
				canEdit @required(action: THROW)
			}
		`,
		jiraVersion,
	);

	const hasReleaseNote = useHasReleaseNote(version.id, !isDevopsFeatureDisabledInFedRamp());

	const isVersionReleased = version.status === RELEASED;

	const [updateVersionReleased] = useMutation<UpdateVersionReleasedMutation>(graphql`
		mutation versionStatus_updateVersionReleasedMutation(
			$input: JiraUpdateVersionReleasedStatusInput!
		) {
			jira {
				updateVersionReleasedStatus(input: $input) @optIn(to: ["JiraUpdateVersionReleasedStatus"]) {
					success
					version {
						status
						canAddAndRemoveIssues
						canEditRelatedWork
					}
				}
			}
		}
	`);

	const [updateVersionArchived, isUpdateArchivedLoading] =
		useMutation<UpdateVersionArchivedMutation>(graphql`
			mutation versionStatus_updateVersionArchivedMutation(
				$input: JiraUpdateVersionArchivedStatusInput!
			) {
				jira {
					updateVersionArchivedStatus(input: $input)
						@optIn(to: ["JiraUpdateVersionArchivedStatus"]) {
						success
						version {
							status
							canAddAndRemoveIssues
							canEditRelatedWork
						}
					}
				}
			}
		`);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [releaseNotesQueryReference, loadReleaseNotesQuery] =
		useQueryLoader<uiReleaseNotesModalQuery>(RELEASE_NOTES_QUERY);
	const loadReleaseNoteModalResource = () => {
		loadReleaseNotesQuery({ versionAri: version.id }, { fetchPolicy: 'store-and-network' });
	};

	const isLoading = isUpdateArchivedLoading || isRefetchLoading;
	const releaseOptions = [
		{ label: formatMessage(messages.releaseOptionLabel), value: RELEASED },
		{ label: formatMessage(messages.unreleaseOptionLabel), value: UNRELEASED },
		{ label: formatMessage(messages.archiveOptionLabel), value: ARCHIVED },
	];
	const archiveOptions = [
		{ label: formatMessage(messages.unarchiveOptionLabel), value: UNARCHIVED },
	];
	const currentOptions = version.status === ARCHIVED ? archiveOptions : releaseOptions;

	const getCurrentValue = (currentStatusValue: JiraVersionStatus) =>
		[...releaseOptions, ...archiveOptions].find((option) => option.value === currentStatusValue);

	const handleChange = (option: VersionStatusOption | null) => {
		if (option === null) {
			return;
		}

		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'selected',
				actionSubject: 'option',
			}),
			option.value,
		);

		switch (option.value) {
			case RELEASED:
				setIsReleaseModalOpen(true);
				if (!isDevopsFeatureDisabledInFedRamp()) {
					loadReleaseNoteModalResource();
				}
				break;
			case UNRELEASED:
				updateVersionReleased({
					variables: {
						input: {
							id: version.id,
							isReleased: false,
						},
					},
					onError: () => {
						fireErrorAnalytics({
							meta: {
								id: 'jira.releases.launch.card.update.released.mutation.error.id',
								packageName: 'jiraSoftwareReleasesLaunchCard',
								teamName: 'nova',
							},
							error: new Error('Unable to update version released status'),
							sendToPrivacyUnsafeSplunk: true,
						});
						showFlag({
							type: 'error',
							title: messages.genericUpdateFailedFlagTitle,
							description: messages.releaseFailedFlagDescription,
							messageId:
								'software-releases-launch-card.ui.version-status.show-flag.error.unreleased.on-error.show-flag.error',
							messageType: 'transactional',
						});
					},
					optimisticResponse: {
						jira: {
							updateVersionReleasedStatus: {
								success: true,
								version: {
									id: version.id,
									status: option.value,
								},
							},
						},
					},
				});
				break;
			case ARCHIVED:
			case UNARCHIVED:
				updateVersionArchived({
					variables: {
						input: {
							id: version.id,
							isArchived: option.value === ARCHIVED,
						},
					},
					onError: () => {
						fireErrorAnalytics({
							meta: {
								id: 'jira.releases.launch.card.update.archived.mutation.error.id',
								packageName: 'jiraSoftwareReleasesLaunchCard',
								teamName: 'nova',
							},
							error: new Error('Unable to update version archived status'),
							sendToPrivacyUnsafeSplunk: true,
						});
						showFlag({
							type: 'error',
							title: messages.genericUpdateFailedFlagTitle,
							description: messages.archiveFailedFlagDescription,
							messageId:
								'software-releases-launch-card.ui.version-status.show-flag.error.unarchived.on-errorshow-flag.error',
							messageType: 'transactional',
						});
					},
				});
				break;
			default:
				break;
		}
	};

	return (
		<Container ref={selectRef}>
			<PopupSelect
				hideSelectedOptions
				components={overrideComponents}
				formatOptionLabel={CustomOptionLabel}
				minMenuWidth={`${selectWidth}px`}
				onChange={handleChange}
				options={currentOptions}
				target={({ isOpen, ref, ...triggerProps }) => (
					<StyledLoadingButton
						{...triggerProps}
						iconAfter={
							<ChevronDownIcon spacing="spacious" label={formatMessage(messages.buttonLabel)} />
						}
						isDisabled={!version.canEdit}
						isLoading={isLoading}
						isSelected={isOpen}
						ref={ref}
						released={isVersionReleased && !isOpen ? 1 : 0}
						shouldFitContainer
						onClick={(_, analyticsEvent) =>
							fireUIAnalytics(analyticsEvent, 'versionStatusDropdown')
						}
					>
						{isVersionReleased ? (
							<ReleasedContentWrapper>
								{GetCurrentStatusLabel(version.status)}
								<ReleaseSucceededIcon />
							</ReleasedContentWrapper>
						) : (
							GetCurrentStatusLabel(version.status)
						)}
					</StyledLoadingButton>
				)}
				value={getCurrentValue(version.status)}
			/>

			<NonCriticalErrorBoundary eventLocation="releaseNotesModal" fallback="flag">
				<ModalTransition>
					<Placeholder name="release-notes-modal" fallback={null}>
						{isReleaseNotesModalOpen && releaseNotesQueryReference && (
							<ReleaseNotesModal
								queryReference={releaseNotesQueryReference}
								onClose={() => setIsReleaseNotesModalOpen(false)}
							/>
						)}
					</Placeholder>
				</ModalTransition>
			</NonCriticalErrorBoundary>

			<ModalTransition>
				{isReleasedModalOpen && (
					<ReleaseModal
						// eslint-disable-next-line jira/deprecations/no-base-url
						baseUrl=""
						// @ts-expect-error - TS2322 - Type '{ baseUrl: string; locale: Locale; projectKey: string; versionId: string; onOpened: (...args: any[]) => void; onCancel: () => void; onSuccess: () => void; onError: () => void; }' is not assignable to type 'IntrinsicAttributes & StateProps & Handlers & { children?: ReactNode; }'.
						locale={locale}
						projectKey={version.project.key}
						versionId={version.versionId}
						onOpened={noop}
						onCancel={() => {
							setIsReleaseModalOpen(false);
						}}
						onSuccess={(_updatedVersion: Version, params: SuccessParams | undefined) => {
							refetch();
							setIsReleaseModalOpen(false);
							if (params && params.shouldRedirectToCreateReleaseNotes) {
								setIsReleaseNotesModalOpen(true);
							}
						}}
						onError={() => {
							showFlag({
								type: 'error',
								title: formatMessage(messages.genericUpdateFailedFlagTitle),
								description: formatMessage(messages.releaseFailedFlagDescription),
								messageId:
									'software-releases-launch-card.ui.version-status.show-flag.error.release-modal.on-error.show-flag.error',
								messageType: 'transactional',
							});
							fireErrorAnalytics({
								error: new Error('Failed to release the version'),
								meta: {
									id: 'versionDetailsLaunchCardReleaseModal',
									packageName: 'jiraSoftwareReleasesLaunchCard',
									teamName: 'nova',
								},
								sendToPrivacyUnsafeSplunk: true,
							});
						}}
						shouldShowCreateReleaseNotesOption={!isDevopsFeatureDisabledInFedRamp()}
						hasReleaseNote={hasReleaseNote ?? false}
					/>
				)}
			</ModalTransition>
		</Container>
	);
};

const ReleaseSucceededIcon = () => (
	<CheckIcon label="" color={RELEASED_TEXT_COLOR} LEGACY_size="small" />
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
const styles = css({ '&&&': { padding: '0px' } });

const RELEASED_BACKGROUND_COLOR = token('color.background.success');

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ReleasedContentWrapper = styled.div({
	alignItems: 'center',
	display: 'flex',
	gap: token('space.050'),
	justifyContent: 'center',
});

export const RELEASED_TEXT_COLOR = token('color.text');

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledLoadingButton = styled(LoadingButton)<{ released: number }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&&': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		background: (props) => (props.released ? RELEASED_BACKGROUND_COLOR : ''),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		color: (props) => (props.released ? RELEASED_TEXT_COLOR : ''),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		height: `${gridSize * 5}px`,
		display: 'flex',
		alignItems: 'center',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const VersionStatusOptionLabel = styled.span<{ isArchiveOption: boolean }>({
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	borderTop: ({ isArchiveOption }) =>
		isArchiveOption ? `2px solid ${token('color.border')}` : 'none',
	cursor: 'pointer',
	display: 'flex',
	paddingTop: token('space.100'),
	paddingRight: token('space.300'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.300'),
});
